import type { TFunction } from "i18next";
import type { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import type { UploadedFile } from "@/stores/ChatStore";

import { UPLOAD_POLL_INTERVAL_MS } from "./fileOperations";
import { createStructuredUploadError } from "./validationHelpers";

/** Parser the platform runs over an uploaded file. */
export type UploadParser = "llm" | "ocr";

export interface UploadAndAwaitParseOptions {
  /** The id the upload is tagged with. Chat uploads pass theirs through `uploadChatFile`. */
  chatId: string;
  /** Whether the uploaded document is private to the uploading user. */
  isPrivate: boolean;
  /** Parser the platform runs over the file. */
  parser: UploadParser;
  /**
   * Cancels the upload. It is checked before the upload starts and around each wait
   * between polls; once aborted the call rejects with "Upload cancelled" and polls no
   * further. A request already in flight is not interrupted. Omit it for no cancellation.
   */
  signal?: AbortSignal;
}

const UPLOAD_CANCELLED_MESSAGE = "Upload cancelled";

const throwIfCancelled = (signal: AbortSignal | undefined): void => {
  if (signal?.aborted) throw new Error(UPLOAD_CANCELLED_MESSAGE);
};

/**
 * Uploads one file and polls its parsing job until it leaves `pending`.
 *
 * A failed job (a quarantined file, for example) rejects with an error whose
 * message is the platform's reason, or the generic translated message when it
 * gives none, and which carries the full job status as structured data so
 * `parseFileUploadError` can show the specific reason rather than a generic one.
 *
 * When `options.signal` aborts, the call rejects with an error whose message is
 * "Upload cancelled", before the upload if it is already aborted and otherwise between polls.
 *
 * This does not check the chat id. Chat composers call `uploadChatFile`, which
 * enforces the chat id contract before delegating here.
 *
 * @param platformClient - The agent platform client. When it is absent the upload
 * yields nothing and the call rejects naming the file.
 * @param file - The file to upload.
 * @param options - The id, privacy flag and parser to upload with.
 * @param t - The translation function, for the generic failure message.
 * @returns The uploaded file, with the job result as its id, as `useFileUpload` expects.
 * @throws When the upload yields no response, the parsing job fails, the job
 * completes without a stored id, or the signal aborts.
 */
export const uploadAndAwaitParse = async (
  platformClient: WebuildAiPlatform | null | undefined,
  file: File,
  { chatId, isPrivate, parser, signal }: UploadAndAwaitParseOptions,
  t: TFunction,
): Promise<UploadedFile> => {
  throwIfCancelled(signal);

  const response = await platformClient?.files.upload({
    bodyUploadFileBackgroundPlatformV1DataFilesPost: {
      file,
      summary: file.name,
      tags: [],
      fileType: file.type,
      chatId,
      private: isPrivate,
      parser,
    },
  });

  if (!response) {
    throw new Error(`Upload failed for ${file.name}`);
  }

  let jobStatus = await platformClient?.files.getJobStatus({ jobId: response.job_id });

  while (jobStatus?.status === "pending") {
    throwIfCancelled(signal);
    await new Promise((resolve) => setTimeout(resolve, UPLOAD_POLL_INTERVAL_MS));
    throwIfCancelled(signal);
    jobStatus = await platformClient?.files.getJobStatus({ jobId: response.job_id });
  }

  if (jobStatus?.status === "failed") {
    const errorMessage =
      jobStatus.result ||
      t("fileUpload.errors.generic.message", { defaultValue: "File upload failed" });

    throw createStructuredUploadError(errorMessage, jobStatus);
  }

  // Without a stored id the backend cannot read the file, so report the
  // upload as failed rather than offering a file that would not be sent (PWI-400).
  if (!jobStatus?.result) {
    throw new Error(t("fileUpload.errors.generic.message", { defaultValue: "File upload failed" }));
  }

  return {
    name: file.name,
    size: String(file.size),
    type: file.type,
    id: jobStatus.result,
    status: "completed",
  };
};
